import { useCallback, useEffect, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';

/**
 * Warn users when they try to leave the page with unsaved changes.
 *
 * Works without React Router's data router. Handles:
 * - beforeunload (tab close / refresh)
 * - same-origin <a> clicks
 * - back/forward via popstate (fallback: temporarily cancels, then replays on confirm)
 */
export function useUnsavedChangesWarning(
	initial = false,
	{
		interceptLinks = false,
		message = 'Du har ulagrede endringer. Er du sikker på at du vil forlate siden?',
		title = 'Ulagrede endringer',
	} = {}
) {
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(initial);
	const [showDialog, setShowDialog] = useState(false);

	const dirtyRef = useRef(hasUnsavedChanges);
	dirtyRef.current = hasUnsavedChanges;

	// Function to execute after user confirms (e.g., navigate)
	const pendingNavRef = useRef(null);

	//
	// Warn on hard reload/close
	//
	useEffect(() => {
		if (!hasUnsavedChanges) return;

		const handler = e => {
			e.preventDefault();
			e.returnValue = '';
			return '';
		};

		window.addEventListener('beforeunload', handler);
		return () => window.removeEventListener('beforeunload', handler);
	}, [hasUnsavedChanges]);

	//
	// Intercept same-origin <a> clicks
	//
	useEffect(() => {
		if (!interceptLinks) return;

		const handleClick = e => {
			if (!dirtyRef.current) return;
			if (
				e.metaKey ||
				e.ctrlKey ||
				e.shiftKey ||
				e.altKey ||
				e.button !== 0
			)
				return;

			let el = e.target;
			while (el && el.tagName !== 'A') el = el.parentElement;
			if (!el) return;

			const href = el.getAttribute('href');
			if (
				!href ||
				href.startsWith('#') ||
				el.hasAttribute('download') ||
				(el.target && el.target !== '_self')
			) {
				return;
			}

			const url = new URL(href, window.location.href);
			if (url.origin !== window.location.origin) return;

			e.preventDefault();
			e.stopPropagation();

			pendingNavRef.current = () => {
				window.location.href = href;
			};
			setShowDialog(true);
		};

		document.addEventListener('click', handleClick, true);
		return () => document.removeEventListener('click', handleClick, true);
	}, [interceptLinks]);

	//
	// Back/forward fallback: cancel immediately, then let user confirm
	//
	useEffect(() => {
		const onPopState = () => {
			if (!dirtyRef.current) return;

			// We just moved in history; try to move forward again to "cancel"
			// Then, if user confirms, we'll go back once.
			setTimeout(() => {
				// This won't do anything if we're at the end of the history stack,
				// but it's the best cross-browser fallback we can do without a data router.
				window.history.go(1);
			}, 0);

			pendingNavRef.current = () => {
				window.history.back();
			};
			setShowDialog(true);
		};

		window.addEventListener('popstate', onPopState);
		return () => window.removeEventListener('popstate', onPopState);
	}, []);

	const handleCancel = useCallback(() => {
		pendingNavRef.current = null;
		setShowDialog(false);
	}, []);

	const handleConfirm = useCallback(() => {
		// Mark pristine before proceeding (also disables beforeunload)
		dirtyRef.current = false;
		setHasUnsavedChanges(false);
		setShowDialog(false);

		if (pendingNavRef.current) {
			const go = pendingNavRef.current;
			pendingNavRef.current = null;
			// Let state flush
			setTimeout(go, 0);
		}
	}, []);

	const WarningDialog = useCallback(() => {
		return (
			<Dialog open={showDialog} onClose={handleCancel}>
				<DialogTitle>{title}</DialogTitle>

				<DialogContent>
					<DialogContentText>{message}</DialogContentText>
				</DialogContent>

				<DialogActions>
					<Button onClick={handleCancel}>Avbryt</Button>

					<Button
						onClick={handleConfirm}
						color="error"
						variant="contained"
					>
						Forlat siden
					</Button>
				</DialogActions>
			</Dialog>
		);
	}, [showDialog, handleCancel, handleConfirm, title, message]);

	return [hasUnsavedChanges, setHasUnsavedChanges, WarningDialog];
}
